<script setup>
import { ref, computed, watch } from 'vue';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

import {
  knowledgeContent,
  formatFileSize,
} from '../../helpers/kraftchatAIKnowledgeConfig';

const props = defineProps({
  document: {
    type: Object,
    required: true,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save', 'cancel']);

const fileInput = ref(null);
const title = ref('');
const active = ref(true);
const replacementFile = ref(null);

const syncForm = () => {
  title.value = props.document.title || '';
  active.value = props.document.active ?? true;
  replacementFile.value = null;
};

const handleFileClick = () => fileInput.value?.click();

const handleFileChange = () => {
  const file = fileInput.value?.files[0];
  replacementFile.value = file || null;
};

const handleRemoveReplacement = () => {
  replacementFile.value = null;
  if (fileInput.value) fileInput.value.value = null;
};

const currentFileLabel = computed(
  () => replacementFile.value?.name || props.document.pdf_file_name || ''
);

const currentFileSizeLabel = computed(() => {
  const size = replacementFile.value?.size ?? props.document.pdf_file_size;
  return size ? formatFileSize(size) : '';
});

const handleSubmit = () => {
  emit('save', {
    title: title.value,
    active: active.value,
    file: replacementFile.value,
  });
};

watch(() => props.document, syncForm, { immediate: true });
</script>

<template>
  <form class="space-y-4" @submit.prevent="handleSubmit">
    <Input
      v-model="title"
      :label="knowledgeContent.titleLabel"
      :placeholder="knowledgeContent.titlePlaceholder"
      size="md"
      type="text"
    />

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ knowledgeContent.pdfFileLabel }}
      </label>

      <div
        class="flex items-center justify-between gap-2 rounded-lg border border-n-weak bg-n-solid-1 p-3"
      >
        <div class="min-w-0">
          <p class="truncate text-sm text-n-slate-12">
            {{ currentFileLabel }}
          </p>
          <p class="text-xs text-n-slate-11">{{ currentFileSizeLabel }}</p>
        </div>

        <div class="flex flex-shrink-0 items-center gap-1">
          <NextButton
            :label="
              replacementFile
                ? knowledgeContent.pdfChangeFileLabel
                : knowledgeContent.replacePdfLabel
            "
            icon="i-lucide-upload"
            color="slate"
            variant="ghost"
            size="sm"
            type="button"
            @click="handleFileClick"
          />
          <NextButton
            v-if="replacementFile"
            icon="i-lucide-trash"
            color="slate"
            variant="ghost"
            size="sm"
            type="button"
            @click="handleRemoveReplacement"
          />
        </div>
      </div>

      <input
        ref="fileInput"
        type="file"
        accept="application/pdf,.pdf"
        class="hidden"
        @change="handleFileChange"
      />
    </div>

    <div
      class="flex items-center justify-between gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
    >
      <div class="min-w-0">
        <p class="text-sm font-medium text-n-slate-12">
          {{ knowledgeContent.activeLabel }}
        </p>

        <p class="mt-1 text-sm leading-6 text-n-slate-11">
          {{ knowledgeContent.activeDescription }}
        </p>
      </div>

      <Switch v-model="active" />
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <NextButton
        type="submit"
        :is-loading="isSaving"
        :label="knowledgeContent.saveLabel"
      />

      <NextButton
        type="button"
        outline
        slate
        :disabled="isSaving"
        :label="knowledgeContent.cancelLabel"
        @click="emit('cancel')"
      />
    </div>
  </form>
</template>
